import React from 'react';
import { Text } from '@admitkard/ccl/Text';
import { theme } from '@admitkard/ccl/style';
import { Icon } from '@admitkard/ccl/Icon';
import { Button } from '@admitkard/ccl/Button';
import { ExpansionPanel } from '@admitkard/ccl/ExpansionPanel';
import { env } from '@admitkard/uiutils/env';
import { pgPackages, pgTestPreparations, ugPackageComparisonTable, faqLists } from './ProductPricingConstants';
import { PackageDetails } from '.';
import Link from 'next/link';
import { BeAPart } from 'components/OurSuccessStories/BeAPart';
import { PackagePointsComparisonTable } from '.';

const styles = `
  .pg-product-pricing_container {
    background-image: url(${env.getAssetUrl('images/pricing_page_background.png')});
    background-size: contain;
    background-color: ${theme.palette.grey[10]};
  }
  .pg-product-pricing_packages-text-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-top: 3rem;
    margin-bottom: ${theme.spacingOld(7)};
  }
  .pg-product-pricing_packages-list-container {
    display: flex;
    flex-direction: column;
    align-items: center;
  }
  .pg-product-pricing_packages-container-title {
    font-size: ${theme.fontSize.xxxl};
    margin-bottom: ${theme.spacingOld(6)};
  }
  .pg-product-pricing_package-container {
    margin-bottom: ${theme.spacingOld(6)};
  }
  .pg-product-pricing_back-link {
    position: relative;
    top: 2rem;
    cursor: pointer;
  }
  .pg-product-pricing_compare-link-title {
    font-size: ${theme.fontSize.xl};
    margin-bottom: ${theme.spacingOld(7)};
  }
  .pg-product-pricing_compare-link-container {
    display: flex;
    flex-direction: column;
    align-items: center;
  }
  .pg-product-pricing_hero-image-container {
    margin-top: ${theme.spacingOld(5)};
    margin-bottom: ${theme.spacingOld(7)};
  }
  .pg-product-pricing_comparison-container {
    display: flex;
    flex-direction: column;
    align-items: center;
  }
  .pg-product-pricing_comparison-table-container {
    background-color: ${theme.palette.common.white};
    border-radius: ${theme.shape.borderRadius};
    box-shadow: ${theme.shadows[4]};
    margin-top: ${theme.spacingOld(7)};
    padding: ${theme.spacingOld(6)} ${theme.spacingOld(5)};
  }
  .pg-product-pricing_comparison-table {
    margin-bottom: ${theme.spacingOld(7)};
  }
  .pg-product-pricing_comparison-table:last-child {
    margin-bottom: 0;
  }
  .pg-product-pricing_faqs-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding-top: ${theme.spacingOld(6)};
  }
  .pg-product-pricing_faq-lists-container {
    background-color: ${theme.palette.common.white};
    margin-top: ${theme.spacingOld(6)};
  }
  .pg-product-pricing_view-more-faq {
    font-size: ${theme.fontSize.m};
    margin: ${theme.spacingOld(6)} 0;
    text-decoration: underline;
    color: #222753;
    font-weight: 600;
  }
  @media only screen and (min-width: 1024px) {
    .pg-product-pricing_back-link {
      top: 3rem;
    }
    .pg-product-pricing_packages-container-title {
      font-size: 2.25rem;
      margin-bottom: ${theme.spacingOld(4)};
      width: 50%;
    }
    .pg-product-pricing_packages-text-container {
      margin-bottom: 5rem;
    }
    .pg-product-pricing_packages-list-container {
      flex-direction: row; 
      justify-content: space-around;
    }
    .pg-product-pricing_compare-link-title {
      margin-top: ${theme.spacingOld(6)};
      font-size: 2.25rem;
    }
    .pg-product-pricing_hero-image-container {
      margin-top: ${theme.spacingOld(7)};
      margin-bottom: 5rem;
    }
    .pg-product-pricing_comparison-table-container {
      padding: ${theme.spacingOld(7)} ${theme.spacingOld(6)};
    }
    .pg-product-pricing_comparison-table {
      margin-bottom: 7rem;
    }
    .pg-product-pricing_comparison-table:last-child {
      margin-bottom: 0;
    }
    .pg-product-pricing_faqs-container {
      padding-top: 3.5rem;
    }
    .pg-product-pricing_faq-lists-container {
      margin-top: ${theme.spacingOld(5)};
    }
    .pg-product-pricing_view-more-faq {
      margin: ${theme.spacingOld(7)} 0;
      cursor: pointer;
    }
  }
`;

interface PricingState {
  allFaqListsVisible: boolean;
}

class PgProductPricing extends React.Component {

  state: PricingState = {
    allFaqListsVisible: false,
  };

  showAllFaqClick = () => {
    this.setState({
      allFaqListsVisible: true
    });
  };

  render() {
    const getFaqsList = faqLists.map((faq, index) => {
      return <ExpansionPanel
        key={index}
        title={faq.title}
        summary={faq.description}
      />;
    });
    return <div className="pg-product-pricing_container">
      <div className="pg-product-pricing_packages-container container">
        <Link href='/company/product-pricing-description-policy' as='/company/product-pricing-description-policy'>
          <Text align='left' variant='s' className="pg-product-pricing_back-link" weight='bold'>
            <span>
              <Icon name='left' size='small' />
              Back to plans
            </span>
          </Text>
        </Link>
        <div className="pg-product-pricing_packages-text-container">
          <Text display='block' weight='bold' align="center" className="pg-product-pricing_packages-container-title">
            Flexible packages for your PG Degree Abroad
          </Text>
          <Text display='block' align='center' className="pg-product-pricing_packages-container-description">
            Choose from a range of packages made just for you!
          </Text>
        </div>
        <div className="pg-product-pricing_packages-list-container">
          {pgPackages.map((pgPackage) => {
            return <div key={pgPackage.packageName} className="pg-product-pricing_package-container">
              <PackageDetails
                packageName={pgPackage.packageName}
                packagePrice={pgPackage.packagePrice}
                packageDescription={pgPackage.packageDescription}
                buttonText="GET STARTED"
                buttonHref="/student/login"
                packageDetails={pgPackage.packageDetails}
              />
            </div>;
          })}
        </div>
      </div>
      <div className="container pg-product-pricing_compare-link-container">
        <Text weight='bold' className="pg-product-pricing_compare-link-title" display='block' align='center'>
          Want to Get More Details?
        </Text>
        <Link href='#detailed-comparison' as="#detailed-comparison">
          <Button variant='contained' color='primary'>
            View Detailed Comparison
          </Button>
        </Link>
      </div>
      <div className="pg-product-pricing_packages-container container">
        <div className="pg-product-pricing_packages-text-container">
          <Text display='block' weight='bold' align="center" className="pg-product-pricing_packages-container-title">
            Test Preparation
          </Text>
          <Text display='block' align='center' className="pg-product-pricing_packages-container-description">
            Ace all your standard test with AdmitKard
          </Text>
        </div>
        <div className="pg-product-pricing_packages-list-container">
          {pgTestPreparations.map((pgTest) => {
            return <div key={pgTest.packageName} className="pg-product-pricing_package-container">
              <PackageDetails
                packageName={pgTest.packageName}
                packagePrice={pgTest.packagePrice}
                packageDescription={pgTest.packageDescription}
                buttonText="GET STARTED"
                buttonHref="/student/login"
                packageDetails={pgTest.packageDetails}
              />
            </div>;
          })}
        </div>
      </div>
      <div className="pg-product-pricing_hero-image-container">
        <BeAPart
          title='Confused About your Plan?'
          description='Connect with your AK Expert now and get personalized plans suited for you.'
          buttonText='Get in touch'
          buttonLink='/student/login/register?utm_source=website&utm_medium=direct&utm_campaign=pricing-page'
        />
      </div>
      <div id="detailed-comparison" className="container pg-product-pricing_comparison-container">
        <Text display='block' weight='bold' align="center" className="pg-product-pricing_packages-container-title">
          Customized Study Plan for Every Student
        </Text>
        <Text display='block' align='center' className="pg-product-pricing_packages-container-description">
          A detailed feature list of what we offer
        </Text>
        <div className="pg-product-pricing_comparison-table-container">
          {ugPackageComparisonTable.map((ugSinglePackageComparison) => {
            return <div key={ugSinglePackageComparison.pointName} className="pg-product-pricing_comparison-table">
              <PackagePointsComparisonTable tableData={ugSinglePackageComparison} />
            </div>;
          })}
        </div>
      </div>
      <div className="container pg-product-pricing_faqs-container">
        <Text display='block' weight='bold' align="center" className="pg-product-pricing_packages-container-title">
          FAQs
        </Text>
        <Text display='block' align='center' className="pg-product-pricing_packages-container-description">
          Featured Questions
        </Text>
        <div className="pg-product-pricing_faq-lists-container">
          {!this.state.allFaqListsVisible ? getFaqsList.slice(0, 4) : getFaqsList}
        </div>
        {!this.state.allFaqListsVisible &&
        <Text display='block' align='center' className="pg-product-pricing_view-more-faq" weight='bold' onClick={this.showAllFaqClick}>
          View More FAQs
        </Text>
        }
      </div>
      <style>{styles}</style>
    </div>;
  }
}

export default PgProductPricing;
